import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, TextInput, Select, Switch, Textarea } from '../../components/ui';
import { useCustomField, updateCustomField } from './useData';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const FieldConfigurator: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const fieldId = id ? parseInt(id, 10) : null;
  const { data: field, isLoading, mutate } = useCustomField(fieldId || 0);

  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [validation, setValidation] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setOptions(Array.isArray(field.options) ? field.options : []);
      setValidation(field.validation || null);
    }
  }, [field]);

  const handleAddOption = () => {
    if (newOption.trim()) {
      setOptions([...options, newOption.trim()]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!field) return;

    setIsSaving(true);
    try {
      await updateCustomField(field.id, {
        ...field,
        options: options.length > 0 ? options : undefined,
        validation: validation || undefined,
      });
      await mutate();
      showSuccess('Custom field updated successfully');
    } catch (error: any) {
      showError(error.message || 'Failed to update custom field');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" message="Loading custom field..." />
      </div>
    );
  }

  if (!field) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Custom field not found</h2>
          <p className="text-gray-600 mb-4">The custom field you are looking for does not exist.</p>
          <Button onClick={() => navigate('/custom-fields')}>Back to Custom Fields</Button>
        </div>
      </div>
    );
  }

  const showOptions = field.fieldType === 'select' || field.fieldType === 'checkbox';
  const showValidation = field.fieldType === 'text' || field.fieldType === 'number' || field.fieldType === 'textarea';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configure: {field.fieldLabel}</h1>
        <p className="text-gray-600">{field.entityType} - {field.fieldType}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Options Configuration */}
        {showOptions && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options {field.fieldType === 'select' ? '(for dropdown)' : '(for checkbox)'}
            </label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <TextInput
                    value={option}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[index] = e.target.value;
                      setOptions(updated);
                    }}
                    className="flex-1"
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveOption(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <TextInput
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="Enter option value"
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button variant="outline" onClick={handleAddOption}>
                  Add Option
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Validation Rules */}
        {showValidation && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Validation Rules
            </label>
            <div className="space-y-4">
              {field.fieldType === 'text' || field.fieldType === 'textarea' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Min Length</label>
                      <TextInput
                        type="number"
                        value={validation?.minLength || ''}
                        onChange={(e) =>
                          setValidation({
                            ...validation,
                            minLength: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                        placeholder="Minimum length"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Length</label>
                      <TextInput
                        type="number"
                        value={validation?.maxLength || ''}
                        onChange={(e) =>
                          setValidation({
                            ...validation,
                            maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                        placeholder="Maximum length"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Pattern (Regex)</label>
                    <TextInput
                      value={validation?.pattern || ''}
                      onChange={(e) =>
                        setValidation({
                          ...validation,
                          pattern: e.target.value || undefined,
                        })
                      }
                      placeholder="e.g., ^[A-Za-z]+$"
                    />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Min Value</label>
                    <TextInput
                      type="number"
                      value={validation?.min || ''}
                      onChange={(e) =>
                        setValidation({
                          ...validation,
                          min: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="Minimum value"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Max Value</label>
                    <TextInput
                      type="number"
                      value={validation?.max || ''}
                      onChange={(e) =>
                        setValidation({
                          ...validation,
                          max: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="Maximum value"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.fieldLabel} {field.isRequired && <span className="text-red-500">*</span>}
            </label>
            {field.fieldType === 'text' && (
              <TextInput placeholder="Enter value" disabled />
            )}
            {field.fieldType === 'number' && (
              <TextInput type="number" placeholder="Enter number" disabled />
            )}
            {field.fieldType === 'date' && (
              <TextInput type="date" disabled />
            )}
            {field.fieldType === 'select' && (
              <Select
                disabled
                value=""
                onChange={() => {}}
                options={[
                  { value: '', label: 'Select an option' },
                  ...options.map((option) => ({ value: option, label: option })),
                ]}
              />
            )}
            {field.fieldType === 'checkbox' && (
              <Switch disabled />
            )}
            {field.fieldType === 'textarea' && (
              <Textarea rows={3} placeholder="Enter text" disabled />
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/custom-fields')}>
          Cancel
        </Button>
        <Button variant="default" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default FieldConfigurator;
